import React, { useState, useEffect } from "react";
import { Box, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { CustomStyledInputLabel, CustomStyledRadio } from "../../../../../../shared/components/form-builder/form-styles";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import { Utills } from "../../../../../../shared/app-configs/utilit";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";

interface InputFarmTraderDetails {
  inputFarmSCNo: string;
  inputFarmTCNo: string;
  inputTraderTCNoForOrganicMaterial: string;
  inputTCOrganicFarmStandards: string;
}

interface OCSFirstProcessorData {
  inputTcOCSFirstProcessor: string; // "Yes" or "No"
  tableData: InputFarmTraderDetails[];
}

interface OcsProcessorProps {
  ocsProcessorData: OCSFirstProcessorData;
  setOcsProcessorData: (data: OCSFirstProcessorData) => void;
}

// Define Yup validation schema for each row


const OcsProcessor: React.FC<OcsProcessorProps> = ({
  ocsProcessorData,
  setOcsProcessorData,
}) => {
  const [data, setData] = useState<InputFarmTraderDetails[]>(ocsProcessorData.tableData);
  const [tableData, setTableData] = useState<InputFarmTraderDetails[]>(ocsProcessorData.tableData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { updateField } = useFormActions("input-tc-product-details");
  const [organicFarmStandardsData, setOrganicFarmStandards] = useState([]);
  const { isRestrictPopUpSave, setRestrictPopUpSave, getTCStandardGOTS } = useTransactionCertificateStore();

  // Sync local state with parent state
  useEffect(() => {
    setOcsProcessorData({ ...ocsProcessorData, inputTcOCSFirstProcessor: ocsProcessorData.tableData.length > 0 ? 'Y' : 'N' });
    setData(ocsProcessorData.tableData);
  }, [ocsProcessorData.tableData]);
  const getOrganicFarmStandards = async () => {
    try {
      const result: any = await dtrackItService.getOrganicFarmStandards()
      const orgFStd = result.data.map((t: any) => ({ value: t.code_1, label: `${t.description_1} (${t.code_1})` }))
      setOrganicFarmStandards(orgFStd);
    } catch (err) {
      console.error(`Error in Fetching Organic Farm Standard Code ${err}`)
    }
  };
  useEffect(() => {
    getOrganicFarmStandards()
  }, []);
  const validationSchema = Yup.object().shape({
    inputFarmSCNo:getTCStandardGOTS ? Yup.string().nullable() : ocsProcessorData.inputTcOCSFirstProcessor=='Y' ? Yup.string().required("Input Farm Sc No. is Required"): Yup.string().nullable(),
    inputFarmTCNo:getTCStandardGOTS ? Yup.string().nullable() : ocsProcessorData.inputTcOCSFirstProcessor=='Y' ? Yup.string().required("Input Farm Tc No. is Required"): Yup.string().nullable(),
    inputTraderTCNoForOrganicMaterial:getTCStandardGOTS ? Yup.string().nullable() : ocsProcessorData.inputTcOCSFirstProcessor=='Y' ? Yup.string().required("Input Trader TC No. For Organic Material is Required"): Yup.string().nullable(),
    inputTCOrganicFarmStandards:getTCStandardGOTS ? Yup.string().nullable() : ocsProcessorData.inputTcOCSFirstProcessor=='Y' ? Yup.string().required("Input TC Organic Farm Standards is Required"): Yup.string().nullable(),
  });
  useEffect(() => {
    data.forEach((item: any, index) => {
      item.tcProductRawMaterialOrder = index + 1;
    });
    updateField("input-tc-ocs-processor", data)
  }, [data])

  const formik = useFormik<InputFarmTraderDetails>({
    initialValues: {
      inputFarmSCNo: "",
      inputFarmTCNo: "",
      inputTraderTCNoForOrganicMaterial: "",
      inputTCOrganicFarmStandards: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
      setOcsProcessorData({ ...ocsProcessorData, tableData: tableData });
    },
  });

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const updatedData = {
      ...ocsProcessorData,
      inputTcOCSFirstProcessor: value,
      tableData: value === "No" ? [] : data, // Clear table data if "No"
    };
    setOcsProcessorData(updatedData);
  };

  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    const updatedData = data.map((row, index) =>
      index === rowIndex ? { ...row, [columnId]: value } : row
    );
    setData(updatedData);
    // setOcsProcessorData({ ...ocsProcessorData, tableData: updatedData });
  };

  const handleAddNewRow = () => {
    const newRow: InputFarmTraderDetails = {
      inputFarmSCNo: "",
      inputFarmTCNo: "",
      inputTraderTCNoForOrganicMaterial: "",
      inputTCOrganicFarmStandards: "",
    };
    const updatedData = [...data, newRow];
    setData(updatedData);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
    setRestrictPopUpSave(true);
    // setOcsProcessorData({ ...ocsProcessorData, tableData: updatedData });
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const handleSaveRow = () => {
    if (editingRowIndex === null || !formik.isValid) return;

    const updatedRow = formik.values;
    const updatedData = data.map((row, index) =>
      index === editingRowIndex ? updatedRow : row
    );
    setData(updatedData);
    setTableData(updatedData);
    setOcsProcessorData({ ...ocsProcessorData, tableData: updatedData });
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
  };

  const columns: EditableColumnDef<InputFarmTraderDetails>[] = [
    {
      accessorKey: "inputFarmSCNo",
      minSize: 30,
      maxSize: 60,
      header: "Farm SC No.",
      Header: getTCStandardGOTS ? "Farm SC No." : (ocsProcessorData.inputTcOCSFirstProcessor == 'Y' ? <span> <i style={{ color: 'red' }}>* </i>Farm SC No</span> : ""),
                renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputFarmSCNo"
          label=""
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC No."
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "inputFarmTCNo",
      header: "Farm TC No.",
      Header: getTCStandardGOTS ? "Farm TC No." : (ocsProcessorData.inputTcOCSFirstProcessor == 'Y' ? <span> <i style={{ color: 'red' }}>* </i>Farm TC No</span> : ""),
      minSize: 30,
      maxSize: 60,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputFarmTCNo"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter TC No."
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "inputTraderTCNoForOrganicMaterial",
      header: "Trader TC No.",
      Header: getTCStandardGOTS ? "Trader TC No." : (ocsProcessorData.inputTcOCSFirstProcessor == 'Y' ? <span> <i style={{ color: 'red' }}>* </i>Trader TC No</span> : ""),
      minSize: 30,
      maxSize: 60,
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="inputTraderTCNoForOrganicMaterial"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter Trader TC No."
          sx={{ mb: 2 }}
        />
      ),
    },
    {
      accessorKey: "inputTCOrganicFarmStandards",
      minSize: 30,
      maxSize: 60,
      header: "Organic Farm Standards",
      Header: getTCStandardGOTS ? "Organic Farm Standards" : (ocsProcessorData.inputTcOCSFirstProcessor == 'Y' ? <span> <i style={{ color: 'red' }}>* </i>Organic Farm Standards</span> : ""),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          enableSearch={true}
          name="inputTCOrganicFarmStandards"
          value={value}
          options={organicFarmStandardsData}
          onChange={(e: any) => {
            onChange(e.target.value)
          }}
        />
        // <CustomTextField
        //   name="inputTCOrganicFarmStandards"
        //   value={value}
        //   onChange={(e) => onChange(e.target.value)}
        //   placeholder="Enter Standards"
        //   sx={{ mb: 2 }}
        // />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setOcsProcessorData({ ...ocsProcessorData, tableData: updatedData });
  };

  return (
    <div>
      <Box component="main" sx={{ flexGrow: 1, p: 1, my: 4 }}>
        <CustomStyledInputLabel>
          Is the input from an OCS first processor?
        </CustomStyledInputLabel>
        <FormControl>
          <RadioGroup
            row
            value={ocsProcessorData.inputTcOCSFirstProcessor}
            onChange={handleRadioChange}
          >
            <FormControlLabel value="Y" control={<CustomStyledRadio />} label="Yes" />
            <FormControlLabel value="N" control={<CustomStyledRadio />} label="No" />
          </RadioGroup>
        </FormControl>

        {/* Display table only if "Yes" is selected */}
        {ocsProcessorData.inputTcOCSFirstProcessor === "Y" && (
          <>
            <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
              <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled = {isRestrictPopUpSave}>
                Add New Row
              </StyledButton>
            </Box>
            <EditableTable
              data={data}
              columns={columns}
              onSaveRow={handleSaveRow}
              onCellChange={handleCellChange}
              editingRowIndex={editingRowIndex}
              onEditRow={handleEditRow}
              onCancelEdit={handleCancelEdit}
              onDeleteRow={handleDeleteRow}
            />
          </>
        )}
      </Box>
    </div>
  );
};

export default OcsProcessor;
